import { useState, useEffect } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { listsEqualById } from '../lib/listDiff';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { FileText, Eye, Trash2, Loader2, Search, Star, User, Send, RefreshCw, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { DevisStatusHistoryDialog } from '../components/devis/DevisStatusHistoryDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const DEVIS_STATUTS = {
    a_envoyer: { label: 'À envoyer', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    envoye: { label: 'Envoyé', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    relance_1: { label: 'Relancé 1', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    relance_2: { label: 'Relancé 2', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    relance_3: { label: 'Relancé 3', color: 'bg-red-100 text-red-700 border-red-200' },
    interesse: { label: 'Intéressé', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    accepte: { label: 'Accepté', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    refuse: { label: 'Refusé', color: 'bg-red-100 text-red-700 border-red-200' },
    sans_reponse: { label: 'Sans réponse', color: 'bg-slate-100 text-slate-600 border-slate-200' },
};

function timeAgo(dateStr) {
    if (!dateStr) return '-';
    try {
        const now = new Date();
        const date = new Date(dateStr.endsWith('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z');
        const diffMs = now - date;
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);
        const diffWeek = Math.floor(diffDay / 7);
        const diffMonth = Math.floor(diffDay / 30);
        if (diffSec < 60) return "à l'instant";
        if (diffMin < 60) return `il y a ${diffMin} min`;
        if (diffHour < 24) return `il y a ${diffHour}h`;
        if (diffDay < 7) return `il y a ${diffDay}j`;
        if (diffWeek < 5) return `il y a ${diffWeek} sem.`;
        if (diffMonth < 12) return `il y a ${diffMonth} mois`;
        return `il y a ${Math.floor(diffDay / 365)} an(s)`;
    } catch {
        return '-';
    }
}

function daysUntil(dateStr) {
    if (!dateStr) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
}

export function DevisPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [dossiers, setDossiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterStatut, setFilterStatut] = useState('all');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyDevis, setHistoryDevis] = useState(null);
    const [aEnvoyerTotal, setAEnvoyerTotal] = useState(0);
    const [relanceTotal, setRelanceTotal] = useState(0);
    const [interesseTotal, setInteresseTotal] = useState(0);

    const fetchDossiers = async (silent = false) => {
        try {
            const { data } = await axios.get(`${API}/api/devis/dossiers`, { withCredentials: true });
            setDossiers(prev => listsEqualById(prev, data) ? prev : data);
        } catch {
            if (!silent) toast.error('Erreur lors du chargement des devis');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDossiers();
        axios.get(`${API}/api/inventaires/devis-a-envoyer`, { withCredentials: true })
            .then(({ data }) => setAEnvoyerTotal(data.length))
            .catch(() => {});
        axios.get(`${API}/api/devis/relances`, { withCredentials: true })
            .then(({ data }) => {
                // On affiche le nombre de dossiers (clients) uniques car la page
                // Relances regroupe par dossier (1 client = 1 ligne).
                const uniqueDossiers = new Set(
                    (data || []).map(i => i.inventaire_id || `devis_${i.devis_id}`)
                ).size;
                setRelanceTotal(uniqueDossiers);
            })
            .catch(() => {});
        axios.get(`${API}/api/devis/interesses`, { withCredentials: true })
            .then(({ data }) => setInteresseTotal(data.length))
            .catch(() => {});
    }, []);

    useAutoRefresh(() => fetchDossiers(true));

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            // Delete all devis for this inventaire
            const { data: devisList } = await axios.get(`${API}/api/devis/dossier/${deleteTarget}`, { withCredentials: true });
            for (const d of devisList) {
                await axios.delete(`${API}/api/devis/${d.id}`, { withCredentials: true });
            }
            toast.success('Dossier devis supprimé');
            setDeleteTarget(null);
            fetchDossiers();
        } catch {
            toast.error('Erreur lors de la suppression');
        } finally {
            setIsDeleting(false);
        }
    };

    const filtered = dossiers.filter(d => {
        const matchSearch = !search || (d.client_nom || '').toLowerCase().includes(search.toLowerCase());
        let matchStatut = filterStatut === 'all';
        if (!matchStatut) {
            if (filterStatut === 'relance') {
                matchStatut = (d.statuts || []).some(s => s.startsWith('relance_')) || d.relance_a_faire;
            } else {
                matchStatut = (d.statuts || []).includes(filterStatut);
            }
        }
        return matchSearch && matchStatut;
    });

    const getMainStatut = (d) => {
        const statuts = d.statuts || [];
        return statuts.includes('accepte') ? 'accepte'
            : statuts.includes('interesse') ? 'interesse'
            : statuts.includes('envoye') ? 'envoye'
            : statuts.includes('relance_1') ? 'relance_1'
            : statuts.includes('relance_2') ? 'relance_2'
            : statuts.includes('relance_3') ? 'relance_3'
            : statuts.includes('refuse') ? 'refuse'
            : statuts.includes('sans_reponse') ? 'sans_reponse'
            : statuts[0] || 'a_envoyer';
    };

    // Tri en 4 sections, base sur le statut AFFICHE dans la colonne "Statut" :
    //  1. Badge "À envoyer" (en haut)
    //  2. Autres statuts + date_demenagement future → date la plus proche en premier
    //  3. Autres statuts + date_demenagement passée → date la plus recemment passee en premier
    //  4. Badge "Refusé" (tout en bas)
    const todayStr = new Date().toISOString().split('T')[0];
    const getSectionInfo = (d) => {
        const mainStatut = getMainStatut(d);
        const date = d.date_demenagement || '';
        if (mainStatut === 'a_envoyer') return { section: 1, date };
        if (mainStatut === 'refuse') return { section: 4, date };
        if (date && date >= todayStr) return { section: 2, date };
        return { section: 3, date };
    };
    const sortedFiltered = [...filtered].sort((a, b) => {
        const ia = getSectionInfo(a);
        const ib = getSectionInfo(b);
        if (ia.section !== ib.section) return ia.section - ib.section;
        // Section 1 : par date de derniere modification (le plus recent en haut)
        if (ia.section === 1) {
            return (b.derniere_modification || '').localeCompare(a.derniere_modification || '');
        }
        // Section 2 : date la plus proche en premier (asc)
        if (ia.section === 2) {
            return (ia.date || '').localeCompare(ib.date || '');
        }
        // Sections 3 & 4 : date la plus recemment passee en premier (desc)
        return (ib.date || '').localeCompare(ia.date || '');
    });

    const aEnvoyerCount = aEnvoyerTotal;
    const relanceCount = relanceTotal;
    const interesseCount = interesseTotal;

    const handleCardFilter = (statut) => {
        setFilterStatut(prev => prev === statut ? 'all' : statut);
    };

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="devis-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1
                        className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                        data-testid="devis-title"
                    >
                        Devis
                    </h1>
                    <p className="mt-1 text-slate-500">
                        Suivez vos dossiers clients et gérez les devis
                    </p>
                </div>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="devis-stats">
                <Card
                    className="rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md bg-white border-amber-200"
                    onClick={() => navigate('/devis-a-envoyer')}
                    data-testid="devis-stat-a-envoyer"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                            <Send size={20} className="text-amber-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-amber-700">{aEnvoyerCount}</p>
                            <p className="text-xs text-slate-500">Devis à envoyer</p>
                        </div>
                    </CardContent>
                </Card>
                <Card
                    className="rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md bg-white border-orange-200"
                    onClick={() => navigate('/relances')}
                    data-testid="devis-stat-relance"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                            <RefreshCw size={20} className="text-orange-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-orange-700">{relanceCount}</p>
                            <p className="text-xs text-slate-500">Relance à effectuer</p>
                        </div>
                    </CardContent>
                </Card>
                <Card
                    className="rounded-xl border shadow-sm cursor-pointer transition-all hover:shadow-md bg-white border-cyan-200"
                    onClick={() => navigate('/interesses')}
                    data-testid="devis-stat-interesse"
                >
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
                            <UserCheck size={20} className="text-cyan-600" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-cyan-700">{interesseCount}</p>
                            <p className="text-xs text-slate-500">Client intéressé</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Rechercher un client..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="devis-search"
                            />
                        </div>
                        <Select value={filterStatut} onValueChange={setFilterStatut}>
                            <SelectTrigger className="w-full sm:w-[200px]" data-testid="devis-filter-statut">
                                <SelectValue placeholder="Filtrer par statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les statuts</SelectItem>
                                {Object.entries(DEVIS_STATUTS).map(([key, { label }]) => (
                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Devis Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <FileText size={20} className="text-[#2e7cd6]" />
                        Liste des devis ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            Aucun devis trouvé
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-0">
                            <Table data-testid="devis-table" className="min-w-[700px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead className="w-10"></TableHead>
                                        <TableHead>Commercial</TableHead>
                                        <TableHead>Jours restants</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Modifié</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {sortedFiltered.map(dossier => {
                                        const jours = daysUntil(dossier.date_demenagement);
                                        // Pick the most relevant statut from all devis
                                        const statuts = dossier.statuts || [];
                                        const mainStatut = statuts.includes('accepte') ? 'accepte'
                                            : statuts.includes('envoye') ? 'envoye'
                                            : statuts.includes('relance_1') ? 'relance_1'
                                            : statuts.includes('relance_2') ? 'relance_2'
                                            : statuts.includes('relance_3') ? 'relance_3'
                                            : statuts.includes('refuse') ? 'refuse'
                                            : statuts.includes('sans_reponse') ? 'sans_reponse'
                                            : statuts[0] || 'a_envoyer';
                                        const statutInfo = DEVIS_STATUTS[mainStatut] || DEVIS_STATUTS.a_envoyer;
                                        return (
                                            <TableRow
                                                key={dossier.inventaire_id}
                                                className="cursor-pointer hover:bg-slate-50"
                                                onClick={() => navigate(`/devis/${dossier.inventaire_id}`)}
                                                data-testid={`dossier-row-${dossier.inventaire_id}`}
                                            >
                                                {/* Client */}
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                                            <User size={20} className="text-slate-500" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-slate-900" data-testid={`dossier-client-${dossier.inventaire_id}`}>
                                                                {dossier.client_nom || 'Sans nom'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                {/* Etoile - ouvre historique */}
                                                <TableCell className="w-10">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setHistoryDevis({ client_nom: dossier.client_nom, inventaire_id: dossier.inventaire_id });
                                                            setIsHistoryOpen(true);
                                                        }}
                                                        className="p-1 rounded-md hover:bg-blue-50 transition-colors"
                                                        title="Historique des statuts"
                                                        data-testid={`devis-star-${dossier.inventaire_id}`}
                                                    >
                                                        <Star
                                                            size={18}
                                                            className="text-[#2e7cd6] fill-[#2e7cd6]"
                                                        />
                                                    </button>
                                                </TableCell>

                                                {/* Commercial */}
                                                <TableCell>
                                                    {dossier.commercial_name ? (
                                                        <div className="flex items-center gap-2">
                                                            {dossier.commercial_couleur && (
                                                                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: dossier.commercial_couleur }} />
                                                            )}
                                                            <span className="text-slate-600">{dossier.commercial_name}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-400 italic">Non assigné</span>
                                                    )}
                                                </TableCell>

                                                {/* Jours restants */}
                                                <TableCell>
                                                    {jours !== null ? (
                                                        <Badge
                                                            variant="outline"
                                                            className={`border ${
                                                                jours < 0 ? 'bg-red-100 text-red-600 border-red-200' :
                                                                jours <= 7 ? 'bg-orange-100 text-orange-600 border-orange-200' :
                                                                jours <= 30 ? 'bg-amber-100 text-amber-600 border-amber-200' :
                                                                'bg-slate-100 text-slate-600 border-slate-200'
                                                            }`}
                                                        >
                                                            {jours < 0 ? `${Math.abs(jours)}j dépassé` : `${jours}j`}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-slate-400">-</span>
                                                    )}
                                                </TableCell>

                                                {/* Statut (affichage seul) */}
                                                <TableCell>
                                                    <Badge variant="outline" className={`${statutInfo.color} border`} data-testid={`devis-statut-${dossier.inventaire_id}`}>
                                                        {statutInfo.label}
                                                    </Badge>
                                                </TableCell>

                                                {/* Modifié */}
                                                <TableCell className="text-slate-500 text-sm" data-testid={`devis-modified-${dossier.inventaire_id}`}>
                                                    {timeAgo(dossier.derniere_modification)}
                                                </TableCell>

                                                {/* Actions */}
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                navigate(`/devis/${dossier.inventaire_id}`);
                                                            }}
                                                            data-testid={`dossier-view-${dossier.inventaire_id}`}
                                                        >
                                                            <Eye size={16} className="text-slate-600" />
                                                        </Button>
                                                        {user?.role === 'admin' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();
                                                                    if (!window.confirm(`Supprimer le dossier de ${dossier.client_nom || 'ce client'} et tous ses devis ? Action irréversible.`)) return;
                                                                    try {
                                                                        await axios.delete(`${API}/api/inventaires/${dossier.inventaire_id}`, { withCredentials: true });
                                                                        toast.success('Dossier supprimé');
                                                                        fetchDossiers();
                                                                    } catch (err) {
                                                                        toast.error('Erreur: ' + (err?.response?.data?.detail || err?.message));
                                                                    }
                                                                }}
                                                                data-testid={`dossier-delete-${dossier.inventaire_id}`}
                                                                title="Supprimer (admin)"
                                                            >
                                                                <Trash2 size={16} className="text-red-500" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Status History Dialog */}
            <DevisStatusHistoryDialog
                open={isHistoryOpen}
                onOpenChange={setIsHistoryOpen}
                devis={historyDevis}
            />

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteTarget} onOpenChange={open => { if (!open) setDeleteTarget(null); }}>
                <AlertDialogContent data-testid="devis-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer ce dossier devis ? Tous les devis associés seront supprimés. Cette action est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="devis-delete-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={isDeleting} data-testid="devis-delete-confirm">
                            {isDeleting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
